/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Mail, Send } from "lucide-react";
import LegalLayout from "./LegalLayout";
import { COMPANY } from "./_company";
import { api } from "@/lib/api";

const TOPICS = [
  { key: "order", label: "Commande / Livraison" },
  { key: "product", label: "Produit / Boutique" },
  { key: "recipe", label: "Recette / Contribution" },
  { key: "press", label: "Presse / Partenariat" },
  { key: "rgpd", label: "RGPD / Données personnelles" },
  { key: "other", label: "Autre" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "order", order_id: "", message: "" });
  const [state, setState] = useState({ sending: false, sent: false, error: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState({ sending: true, sent: false, error: "" });
    try {
      await api.contact(form);
      setState({ sending: false, sent: true, error: "" });
      setForm({ name: "", email: "", topic: "order", order_id: "", message: "" });
    } catch (err) {
      setState({ sending: false, sent: false, error: err?.response?.data?.detail || "Une erreur est survenue. Réessayez ou écrivez-nous directement par email." });
    }
  };

  return (
    <LegalLayout
      eyebrow="Contact"
      title="Écrivez-nous"
      lastUpdated={COMPANY.effectiveDate}
      intro="Une question sur une recette, une commande, une idée de collaboration ? Notre boîte aux lettres est grande ouverte."
    >
      <div className="not-prose mb-12 grid sm:grid-cols-2 gap-4">
        <a
          href={`mailto:${COMPANY.email}`}
          className="border border-[color:rgba(0,16,48,0.12)] p-5 flex items-center gap-4 hover:border-[color:var(--ink)] transition-colors"
          data-testid="contact-email-link"
        >
          <Mail size={20} className="text-[color:var(--red)]" strokeWidth={1.5} />
          <div>
            <p className="eyebrow">Email direct</p>
            <p className="font-serif-display text-xl mt-1">{COMPANY.email}</p>
          </div>
        </a>
        <div className="border border-[color:rgba(0,16,48,0.12)] p-5">
          <p className="eyebrow">Délai de réponse</p>
          <p className="font-serif-display text-xl mt-1">Sous 48h ouvrées</p>
          <p className="text-xs text-[color:var(--ink-3)] mt-2">Du lundi au vendredi · hors jours fériés</p>
        </div>
      </div>

      <h2>Formulaire</h2>

      {state.sent ? (
        <div data-testid="contact-success" className="not-prose border-l-2 border-[color:var(--palm)] bg-[var(--bg-2)] p-6 my-8">
          <p className="font-serif-display text-2xl mb-2">Message reçu, merci !</p>
          <p className="text-sm text-[color:var(--ink-2)]">
            Nous vous répondons sous 48 heures ouvrées à l'adresse indiquée. Vérifiez aussi votre
            dossier de spam au cas où.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="not-prose space-y-6 my-8" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="label-line">Nom</label>
              <input
                data-testid="contact-name"
                required
                className="input-line"
                value={form.name}
                onChange={set("name")}
              />
            </div>
            <div>
              <label className="label-line">Email</label>
              <input
                data-testid="contact-email"
                required
                type="email"
                className="input-line"
                value={form.email}
                onChange={set("email")}
              />
            </div>
          </div>
          <div>
            <label className="label-line">Sujet</label>
            <select
              data-testid="contact-topic"
              value={form.topic}
              onChange={set("topic")}
              className="input-line"
            >
              {TOPICS.map((tp) => (
                <option key={tp.key} value={tp.key}>{tp.label}</option>
              ))}
            </select>
          </div>
          {form.topic === "order" && (
            <div>
              <label className="label-line">Numéro de commande (si applicable)</label>
              <input
                data-testid="contact-order-id"
                className="input-line"
                value={form.order_id}
                onChange={set("order_id")}
                placeholder="ord_..."
              />
            </div>
          )}
          <div>
            <label className="label-line">Message</label>
            <textarea
              data-testid="contact-message"
              required
              rows={6}
              className="input-line"
              value={form.message}
              onChange={set("message")}
              placeholder="Décrivez-nous votre demande en quelques lignes…"
            />
          </div>

          {state.error && (
            <p data-testid="contact-error" className="text-sm text-[color:var(--red)]">{state.error}</p>
          )}

          <button
            type="submit"
            data-testid="contact-submit"
            disabled={state.sending}
            className="btn-navy"
          >
            {state.sending ? "Envoi…" : <>Envoyer <Send size={14} /></>}
          </button>

          <p className="text-xs text-[color:var(--ink-3)] leading-relaxed">
            Les informations saisies sont utilisées uniquement pour répondre à votre demande.
            Voir notre <a href="/legal/confidentialite" className="underline">Politique de confidentialité</a>.
          </p>
        </form>
      )}
    </LegalLayout>
  );
}
